/** About — institutional story page. CMS-aware later; static today. */
import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpen, FlaskConical, Globe2, ShieldCheck, Sparkles, Mail, MessageCircle, HeartHandshake, PlayCircle } from "lucide-react";
import {
  Container, Section, Eyebrow, DisplayHeading, AccentItalic, Lede, Button, Card, CardBody, Breadcrumb,
} from "../components/ui";
import { usePublicSettings, whatsappHref } from "../lib/usePublicSettings";

// Quick-access panels; WhatsApp / Donate appear only once configured in Admin → Settings → about_links.
function ConnectPanels() {
  const links = usePublicSettings()?.about_links;
  const wa = whatsappHref(links);
  const panels = [
    { id: "research", Icon: FlaskConical, t: "Research", b: "Peer-reviewed papers and studies.", to: "/research" },
    { id: "videos", Icon: PlayCircle, t: "Videos", b: "Teachings and talks on YouTube.", to: "/videos" },
    { id: "contact", Icon: Mail, t: "Contact", b: "Write to our team — we reply personally.", to: "/contact" },
    wa && { id: "whatsapp", Icon: MessageCircle, t: "WhatsApp", b: "Chat with us directly.", href: wa },
    links?.donate_url && { id: "donate", Icon: HeartHandshake, t: "Donate", b: "Support the preservation of the sacred sciences.", href: links.donate_url },
  ].filter(Boolean);
  return (
    <Section padded="sm" data-testid="about-connect">
      <Container>
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${panels.length > 4 ? "lg:grid-cols-5" : panels.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {panels.map(({ id, Icon, t, b, to, href }) => {
            const inner = (
              <Card variant="elevated" interactive className="h-full"><CardBody className="p-6">
                <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-mauve/15 text-mauve"><Icon size={18} /></span>
                <h3 className="font-display text-[20px] mt-4 flex items-center gap-2">{t} <ArrowRight size={14} className="text-mauve" /></h3>
                <p className="text-text-dim text-[13px] mt-1.5 leading-relaxed">{b}</p>
              </CardBody></Card>
            );
            return href
              ? <a key={id} href={href} target="_blank" rel="noopener noreferrer" data-testid={`about-panel-${id}`}>{inner}</a>
              : <Link key={id} to={to} data-testid={`about-panel-${id}`}>{inner}</Link>;
          })}
        </div>
      </Container>
    </Section>
  );
}

const PRINCIPLES = [
  { Icon: ShieldCheck, t: "Lineage-verified", b: "Every course traces back to an authentic teaching lineage." },
  { Icon: BookOpen, t: "Text-grounded", b: "Primary-source scholarship, never paraphrased." },
  { Icon: FlaskConical, t: "Research-driven", b: "We measure what we teach, and revise what we measure." },
  { Icon: Globe2, t: "Globally accessible", b: "60+ countries · multilingual where the texts permit." },
  { Icon: Award, t: "Independently certified", b: "Verifiable certificates with QR-backed validation." },
  { Icon: Sparkles, t: "Devotion meets discipline", b: "Bhakti and pramāṇa, in equal measure." },
];

export function AboutPage() {
  return (
    <>
      <Section padded="sm">
        <Container>
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "About" }]} />
          <Eyebrow className="mt-6">A spiritual sciences university</Eyebrow>
          <DisplayHeading className="mt-5 text-[clamp(36px,5vw,56px)]">
            Where ancient wisdom <br/> meets <AccentItalic>future technology</AccentItalic>.
          </DisplayHeading>
          <Lede className="mt-7 max-w-[68ch]">
            Nikhil Vatika Lab is a global institution dedicated to the rigorous study, preservation
            and advancement of the sacred sciences — Sri Vidya, Tantra, Vedanta, Yoga, Ayurveda,
            Jyotisha and Mantra. We teach with lineage, we publish with peer review, and we build
            with empathy. Our purpose is to make the deepest classical scholarship beautifully
            accessible to thoughtful seekers anywhere in the world.
          </Lede>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild><Link to="/courses">Browse the curriculum <ArrowRight size={16}/></Link></Button>
            <Button asChild variant="ghost"><Link to="/research">Read our research</Link></Button>
          </div>
        </Container>
      </Section>

      <ConnectPanels />

      <Section padded="sm">
        <Container>
          <Eyebrow>How we work</Eyebrow>
          <h2 className="font-display text-[clamp(28px,3.4vw,42px)] mt-3 tracking-[-0.01em]">Six principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {PRINCIPLES.map(({ Icon, t, b }) => (
              <Card key={t} variant="elevated"><CardBody className="p-6">
                <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-mauve/15 text-mauve"><Icon size={18}/></span>
                <h3 className="font-display text-[20px] mt-4">{t}</h3>
                <p className="text-text-dim text-[13.5px] mt-2 leading-relaxed">{b}</p>
              </CardBody></Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section padded="sm">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <Eyebrow>Our story</Eyebrow>
              <h2 className="font-display text-[clamp(26px,3vw,38px)] mt-3 tracking-[-0.01em]">
                A small workshop. <AccentItalic>A wide mission.</AccentItalic>
              </h2>
              <div className="mt-6 space-y-4 text-text-dim text-[15px] leading-[1.85]">
                <p>NVL began as a quiet workshop in Rishikesh — a few scholars, one library, and the conviction that the world's classical spiritual sciences deserved both reverence and rigour.</p>
                <p>Today the platform unites teaching, practice, scholarship, products, services and a 40,000-volume digital library under a single institutional roof. We remain small enough to know every student by name, and large enough to reach 60+ countries.</p>
                <p>We do not chase trends. We do not dilute the source. We simply keep working — slowly, carefully, and with affection — toward the day when every serious seeker has a permanent home for study.</p>
              </div>
            </div>
            <Card variant="elevated"><CardBody className="p-8">
              <Eyebrow>By the numbers</Eyebrow>
              <div className="mt-5 grid grid-cols-2 gap-6">
                {[
                  ["100+", "Spiritual Courses"],
                  ["40K+", "Library Volumes"],
                  ["50+", "Research Papers"],
                  ["60+", "Countries Reached"],
                  ["25K+", "Active Seekers"],
                  ["12", "Senior Faculty"],
                ].map(([n, l]) => (
                  <div key={l}>
                    <div className="font-display text-[32px]" style={{ background: "linear-gradient(135deg, var(--neutral), var(--mauve))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{n}</div>
                    <div className="text-[11.5px] font-mono uppercase tracking-[0.16em] text-text-dim mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </CardBody></Card>
          </div>
        </Container>
      </Section>
    </>
  );
}
